import { SlickGrid, SlickDataView, SlickCheckboxSelectColumn, SlickRowSelectionModel } from "slickgrid";
import $ from "jquery";
import compareMultiType from "./utils/compare-multi-type";
import { compareForSort } from "./utils/table-sort";
import { withLinkWrapper, simpleLinkFormatter } from "./utils/formatters";

function createTotalsProvider(dataView, columns, totalsOptions = {}) {
  const { fields = ["par_value", "gross_consideration"], label = "Totals", cssClasses = "totals-row bg-gray-50 font-semibold" } = totalsOptions;

  const provider = {
    onRowCountChanged: dataView.onRowCountChanged,
    onRowsChanged: dataView.onRowsChanged,
    getLength() { return dataView.getLength() + getTotalsCurrencies().length; },
    getItem(i) {
      const baseLen = dataView.getLength();
      if (i < baseLen) return dataView.getItem(i);
      const totalsOrder = getTotalsCurrencies();
      const idx = i - baseLen;
      const currency = totalsOrder[idx];
      return currency ? computeTotalsRow(currency) : null;
    },
    getItemMetadata(i) {
      const baseLen = dataView.getLength();
      const totalsOrder = getTotalsCurrencies();
      if (i >= baseLen) {
        const idx = i - baseLen;
        if (idx >= 0 && idx < totalsOrder.length) {
          return { cssClasses: `${cssClasses} pointer-events-none select-none hover:!bg-transparent !bg-neutral-100` };
        }
      }
      return (typeof dataView.getItemMetadata === "function")
        ? dataView.getItemMetadata(i)
        : null;
    }
  };

  function computeTotalsRow(currency) {
    const totals = { id: `__totals_${currency}__`, _isTotals: true, _currency: currency };

    const firstDataCol = columns.find(c => c.id !== "_checkbox_selector");
    if (firstDataCol) totals[firstDataCol.field] = `Total ${currency}`;

    const len = dataView.getLength();
    for (const col of columns) {
      const f = col.field;
      if (!f || !fields.includes(f)) continue;

      let sum = 0, hasNum = false;
      for (let r = 0; r < len; r++) {
        const item = dataView.getItem(r);
        if (!item) continue;

        if (item.currency === currency) {
          const v = item[f];
          if (v !== null && v !== undefined && !isNaN(Number(v))) {
            sum += Number(v);
            hasNum = true;
          }
        }
      }
      totals[f] = hasNum ? sum : 0;
    }

    return totals;
  }

  function hasNonZeroTotals(currency) {
    const len = dataView.getLength();
    for (const f of fields) {
      let sum = 0;
      for (let r = 0; r < len; r++) {
        const item = dataView.getItem(r);
        if (!item) continue;
        if (item.currency === currency) {
          const v = item[f];
          if (v !== null && v !== undefined && !isNaN(Number(v))) sum += Number(v);
        }
      }
      if (sum !== 0) return true;
    }
    return false;
  }

  function getTotalsCurrencies() {
    const result = [];
    if (hasNonZeroTotals("AUD")) result.push("AUD");
    if (hasNonZeroTotals("USD")) result.push("USD");
    return result;
  }

  return provider;
}

function selectionStorageRowKey(item) {
  if (item == null) return null;
  const key = item.slug ?? item.id;
  return key != null && key !== "" ? String(key) : null;
}

class SlickGridTable {
  // option properties
  data = [];
  columnDefinitions = [];
  sortDefault = { column: "", direction: "" };
  selectedColumns = [];
  tableTarget = "";
  showCheckbox = false;
  showTotals = false;
  options = {
    editable: false,
    enableAddRow: false,
    enableCellNavigation: true,
    asyncEditorLoading: true,
    autoEdit: false,
    enableAsyncPostRender: true,
    syncColumnCellResize: true,
    autoHeight: true,
    enableAutoResize: true,
    multiColumnSort: true,
    rowHeight: 32,
    enableResponsiveResize: false
  };

  // table properties
  dataView;
  grid;

  // pagination properties
  paginationTarget;
  pageSize;
  currentPage;
  totalCount;
  totalPages;
  onPageNumberChange;
  onPageSizeChange;

  // selected rows properties
  selectedRowData = [];
  selectedRowIds = [];

  // callback properties
  onSortCallback = null;

  // Shared method to transform json data into table data format
  jsonToTableData(data, includeAllFields = false) {
    return data.map((security) => {
      const result = {};

      if (includeAllFields) {
        // Include all fields from the security object
        Object.keys(security).forEach(key => {
          result[key] = security[key];
        });
      } else {
        // Only include fields that are in the selected columns
        this.columnDefinitions.forEach((colDef) => {
          result[colDef.field] = security[colDef.field.replace(/-/g, "_")];
        });
      }

      // Always add the id field (slug preferred; fall back for legacy/partial localStorage rows)
      result["id"] = security.slug ?? security.id;
      return result;
    });
  }

  constructor({
    data,
    columnDefinitions,
    sortDefault,
    selectedColumns,
    tableTarget,
    showCheckbox,
    showTotals,
    totalsOptions,
    searchTerm,
    options,
    paginationOptions,
    highlightedRows,
    bidHighlightedRows,
    askHighlightedRows,
    responsiveOptions,
  }) {
    this.data = data || [];
    this.columnDefinitions = columnDefinitions || [];
    this.sortDefault = sortDefault || { column: "", direction: "" };
    this.selectedColumns = selectedColumns || [];
    this.tableTarget = tableTarget || "";
    this.showCheckbox = showCheckbox || false;
    this.showTotals = showTotals || false;
    this.totalsOptions = totalsOptions || {
      fields: ["par_value", "gross_consideration"],
      label: "Totals",
      cssClasses: "totals-row bg-gray-50 font-semibold"
    };
    this.searchTerm = searchTerm || "";
    this.highlightedRows = highlightedRows || [];
    this.bidHighlightedRows = bidHighlightedRows || [];
    this.askHighlightedRows = askHighlightedRows || [];
    this.options = {
      ...this.options,
      ...options,
    };

    this.responsiveOptions = {
      rowHeight: (options && options.rowHeight) || 32,
      minVisibleRows: 10,
      rowHeaderHeight: 30,
      headerOffsetPx: 250,
      ...(responsiveOptions || {}),
    };

    this.selectionStorageKey = (this.options && this.options.selectedRowDataKey) || 'selectedRowData';
    this.selectionIdsStorageKey = (this.options && this.options.selectedRowsKey) || 'selectedRows';

    // Keep rowData as original untransformed data for storing in localStorage
    this.rowData = this.data;
    this.data = this.jsonToTableData(this.data);

    this.selectedRowData = JSON.parse(localStorage.getItem(this.selectionStorageKey) || '[]');
    this.selectedRowIds = this.selectedRowData.map((row) => selectionStorageRowKey(row)).filter(Boolean);

    if (paginationOptions) {
      this.pageSize = paginationOptions.pageSize || 20;
      this.currentPage = paginationOptions.currentPage || 1;
      this.totalCount = paginationOptions.totalCount || 1;
      this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 0;
      this.paginationTarget = paginationOptions.paginationTarget || "";
      this.onPageNumberChange = paginationOptions.onPageNumberChange || (() => {});
      this.onPageSizeChange = paginationOptions.onPageSizeChange || (() => {});
    }

    this.renderTable();
    this.renderPagination();

    if (this.options.enableResponsiveResize) {
      this.bindResizeHandler();
      this.resizeToViewport();
    }
  }

  renderTable() {
    // Reload selections from localStorage to ensure we have the latest state
    this.selectedRowData = JSON.parse(localStorage.getItem(this.selectionStorageKey) || '[]');
    this.selectedRowIds = this.selectedRowData.map((row) => selectionStorageRowKey(row)).filter(Boolean);

    // Initialize only columns with id included into selectedColumns
    const displayColumns = this.columnDefinitions.filter((col) => this.selectedColumns.includes(col.id));

    // Add checkbox column
    let checkboxSelector;
    if (this.showCheckbox) {
      checkboxSelector = new SlickCheckboxSelectColumn({
        cssClass: "slick-cell-checkboxsel ba-slick-cell",
        width: 40,
      });

      const checkboxColDef = checkboxSelector.getColumnDefinition();

      const originalFormatter = checkboxColDef.formatter;
      checkboxColDef.formatter = (row, cell, value, columnDef, dataContext) => {
        if (dataContext && dataContext._isTotals) {
          return '';
        }
        return originalFormatter ? originalFormatter(row, cell, value, columnDef, dataContext) : '';
      };

      displayColumns.unshift(checkboxColDef);
    }

    // Apply link to first data column if it's not an ISIN or ID column
    if (displayColumns.length > 0) {
      const firstDataColumnIndex = this.showCheckbox ? 1 : 0;
      const firstDataColumn = displayColumns[firstDataColumnIndex];

      if (firstDataColumn &&
          firstDataColumn.id !== "isin" &&
          firstDataColumn.id !== "id") {

        if (firstDataColumn.formatter) {
          // Wrap existing formatter with link functionality
          firstDataColumn.formatter = withLinkWrapper(firstDataColumn.formatter);
        } else {
          // Add simple link formatter for columns without existing formatters
          firstDataColumn.formatter = simpleLinkFormatter;
        }
      }
    }

    // Create the DataView.
    this.dataView = new SlickDataView();

    // Start with all data (selected + non-selected)
    let filteredData = [...this.data];

    // Inject selected securities that are not in the current data
    // This ensures previously selected items appear in watch lists/security lists
    const currentDataIds = new Set(filteredData.map((item) => item.id));
    const missingSelectedItems = this.selectedRowData.filter((item) => {
      const key = selectionStorageRowKey(item);
      return key != null && !currentDataIds.has(key);
    });
    if (missingSelectedItems.length > 0) {
      // Transform the injected items to match the expected column field format (underscores → dashes)
      const transformedMissingItems = this.jsonToTableData(missingSelectedItems, false);
      filteredData = [...transformedMissingItems, ...filteredData];
    }

    // Apply search filter if present
    if (this.searchTerm) {
      const searchTerm = this.searchTerm.toLowerCase();
      filteredData = filteredData.filter((item) => {
        for (const key in item) {
          if (this.selectedColumns.includes(key) && item.hasOwnProperty(key) && item[key] != null) {
            var value = item[key].toString().toLowerCase();
            if (value.indexOf(searchTerm) !== -1) {
              return true;
            }
          }
        }
        return false;
      });
    }

    // Apply sorting to all data - prioritize selected rows at the top
    if (Array.isArray(this.sortDefault) && this.sortDefault.length > 0) {
      // Handle multi-column sort
      filteredData.sort((a, b) => {
        // First, check if one is selected and the other is not
        const aSelected = this.selectedRowIds.includes(a.id);
        const bSelected = this.selectedRowIds.includes(b.id);

        if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
        if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

        // If both have the same selection status, apply original sorting
        for (const { column, direction } of this.sortDefault) {
          const sign = direction === "asc" ? 1 : -1;
          const result = compareMultiType(a[column], b[column]);
          if (result !== 0) return result * sign;
        }
        return 0;
      });
    } else if (this.sortDefault.column) {
      const sortField = this.sortDefault.column;
      const sortDirection = this.sortDefault.direction;

      filteredData.sort((a, b) => {
        // First, check if one is selected and the other is not
        const aSelected = this.selectedRowIds.includes(a.id);
        const bSelected = this.selectedRowIds.includes(b.id);

        if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
        if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

        // If both have the same selection status, apply original sorting
        return compareMultiType(a[sortField], b[sortField], sortDirection);
      });
    } else {
      // Even if no sorting is specified, still prioritize selected rows
      filteredData.sort((a, b) => {
        const aSelected = this.selectedRowIds.includes(a.id);
        const bSelected = this.selectedRowIds.includes(b.id);

        if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
        if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

        return 0; // Maintain original order if both have same selection status
      });
    }

    // Only apply pagination slicing if pagination is enabled
    if (this.pageSize && this.currentPage) {
      const offset = this.pageSize * (this.currentPage - 1);
      filteredData = filteredData.slice(offset, offset + this.pageSize);
    }

    // Apply highlight for securities with inventory
    this.dataView.getItemMetadata = (row) => {
      var item = this.dataView.getItem(row);

      if (this.askHighlightedRows.includes(item.id)) {
        return { cssClasses: '!bg-primary/20 hover:!bg-primary/30' };
      } else if (this.bidHighlightedRows.includes(item.id)) {
        return { cssClasses: '!bg-primary/10 hover:!bg-primary/20' };
      } else if (this.highlightedRows.includes(item.id)) {
        // Fallback if item ID is in highlightedRows array
        return { cssClasses: '!bg-primary/20 hover:!bg-primary/30' };
      } else {
        return null;
      }
    }

    this.dataView.setItems(filteredData);

    // Set the grid from dataView, columns and option
    if (document.getElementById(this.tableTarget)) {
      let dataProvider = this.dataView;
      if (this.showTotals) {
        dataProvider = createTotalsProvider(this.dataView, displayColumns, this.totalsOptions);
      }
      this.grid = new SlickGrid(`#${this.tableTarget}`, dataProvider, displayColumns, this.options);

      // Apply sorting to the grid and dataView
      if (Array.isArray(this.sortDefault) && this.sortDefault.length > 0) {
        // Multi-column sort
        this.grid.setSortColumns(
          this.sortDefault.map(s => ({
            columnId: s.column,
            sortAsc: s.direction === "asc"
          }))
        );

        this.dataView.sort((a, b) => {
          // First, check if one is selected and the other is not
          const aSelected = this.selectedRowIds.includes(a.id);
          const bSelected = this.selectedRowIds.includes(b.id);

          if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
          if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

          // If both have the same selection status, apply original sorting
          for (const { column, direction } of this.sortDefault) {
            const sign = direction === "asc" ? 1 : -1;
            const result = compareMultiType(a[column], b[column]);
            if (result !== 0) return result * sign;
          }
          return 0;
        });
      } else if (this.sortDefault.column) {
        // Single column sort
        this.grid.setSortColumn(this.sortDefault.column, this.sortDefault.direction === "asc");
      }

      if (this.showCheckbox) {
        this.grid.setSelectionModel(new SlickRowSelectionModel({ selectActiveRow: false }));
        this.grid.registerPlugin(checkboxSelector);
      }

      // Calculate selected row indices AFTER sorting is applied
      const selectedRowsIndex = [];
      filteredData.forEach((item, index) => {
        if (this.selectedRowIds.includes(item.id)) {
          selectedRowsIndex.push(index);
        }
      });

      this.grid.setSelectedRows(selectedRowsIndex);

      this.dataView.onRowsChanged.subscribe((e, args) => {
        this.grid.invalidateRows(args.rows);
        this.grid.render();
      });

      this.dataView.syncGridSelection(this.grid, true);

      // Set up selection change handler
      if (this.showCheckbox) {
        this.grid.onSelectedRowsChanged.subscribe((e, args) => {
          const changedRowIndex = args.rows
            .filter((x) => !args.previousSelectedRows.includes(x))
            .concat(args.previousSelectedRows.filter((x) => !args.rows.includes(x)));

          const changedRowIds = changedRowIndex.map((index) => this.dataView.getItem(index).id);

          if (this.onSelectedRowsChangedCallback) {
            this.onSelectedRowsChangedCallback(changedRowIds);
          }

          // Update selected rows in localStorage
          this.updateSelectedRows(changedRowIds, args.rows);
        });
      }

      this.grid.onSort.subscribe((e, args) => {
        const cols = args.sortCols;

        this.dataView.sort((a, b) => {
          // First, check if one is selected and the other is not
          const aSelected = this.selectedRowIds.includes(a.id);
          const bSelected = this.selectedRowIds.includes(b.id);

          if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
          if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

          // If both have the same selection status, apply original sorting
          for (let i = 0; i < cols.length; i++) {
            const { sortCol, sortAsc } = cols[i];
            const field = sortCol.field;
            const sign = sortAsc ? 1 : -1;

            const result = compareMultiType(a[field], b[field]);
            if (result !== 0) {
              return result * sign;
            }
          }
          return 0;
        });

        this.grid.invalidate();
        this.grid.render();

        // Notify parent controller about sort change
        if (this.onSortCallback) {
          this.onSortCallback(e, args);
        }
      });

      if (this.options.enableResponsiveResize) {
        this.resizeToViewport();
      }
    }
  }

  bindResizeHandler() {
    if (this._boundResizeHandler) return;
    this._boundResizeHandler = () => {
      if (this._resizeTimeout) clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => this.resizeToViewport(), 100);
    };
    window.addEventListener('resize', this._boundResizeHandler);
  }

  resizeToViewport() {
    try {
      const container = document.getElementById(this.tableTarget);
      if (!container) return;

      const rowHeight = this.responsiveOptions.rowHeight;
      const minVisibleRows = this.responsiveOptions.minVisibleRows;
      const rowHeaderHeight = this.responsiveOptions.rowHeaderHeight;
      const headerOffsetPx = this.responsiveOptions.headerOffsetPx;

      const minHeight = (minVisibleRows * rowHeight) + rowHeaderHeight;
      const maxHeight = window.innerHeight - headerOffsetPx;
      const calculatedHeight = Math.max(minHeight, maxHeight);

      container.style.height = `${calculatedHeight}px`;

      if (this.grid && typeof this.grid.resizeCanvas === 'function') {
        this.grid.resizeCanvas();
        if (typeof this.grid.autosizeColumns === 'function') {
          this.grid.autosizeColumns();
        }
      }
    } catch (e) {
      if (console && console.warn) console.warn('SlickGridTable resize failed', e);
    }
  }

  updateSelectedRows(changedRowIds, selectedRowIndices) {
    // Get the IDs of the selected rows from the current view
    const currentlySelectedRowIds = selectedRowIndices.map(index => this.dataView.getItem(index).id);

    // Get existing selections from localStorage
    const existingSelectedData = JSON.parse(localStorage.getItem(this.selectionStorageKey) || '[]');

    // Get IDs from the original page data (NOT including injected items from localStorage)
    const rowDataIds = new Set(this.rowData.map(item => item.slug));

    // Get the complete data for currently selected rows that exist in rowData
    const currentlySelectedFullDataItems = this.rowData.filter(item => currentlySelectedRowIds.includes(item.slug));
    const currentlySelectedData = this.jsonToTableData(currentlySelectedFullDataItems, true);

    // Build the final selection
    const finalSelectedData = [];
    const finalSelectedIds = [];
    const addedIds = new Set();

    // First, preserve selections that are:
    // 1. NOT in current rowData (i.e., injected items we can't get fresh data for)
    // 2. AND still selected in the grid
    existingSelectedData.forEach(item => {
      if (!rowDataIds.has(item.id) && currentlySelectedRowIds.includes(item.id)) {
        finalSelectedData.push(item);
        finalSelectedIds.push(item.id);
        addedIds.add(item.id);
      }
    });

    // Then, add selections that ARE in rowData (with fresh data from rowData)
    currentlySelectedData.forEach(item => {
      if (!addedIds.has(item.id)) {
        finalSelectedData.push(item);
        finalSelectedIds.push(item.id);
        addedIds.add(item.id);
      }
    });

    // Save all selections to localStorage
    localStorage.setItem(this.selectionStorageKey, JSON.stringify(finalSelectedData));
    localStorage.setItem(this.selectionIdsStorageKey, JSON.stringify(finalSelectedIds));

    // Update internal state
    this.selectedRowData = finalSelectedData;
    this.selectedRowIds = finalSelectedIds;

    // Dispatch event to update UI count
    document.dispatchEvent(new CustomEvent('securities-selection-changed', {
      detail: { selectedRows: finalSelectedIds }
    }));
  }

  renderPagination() {
    if (!this.paginationTarget) {
      return;
    }

    this.renderPaginationContent();

    // Prev page button handler
    $("#prev-page").on("click", () => {
      if (this.currentPage > 1) {
        this.currentPage--;
        $("#page-number").val(this.currentPage);
        this.renderTable();
        this.renderPagination();
      }
    });

    // Next page button handler
    $("#next-page").on("click", () => {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        $("#page-number").val(this.currentPage);
        this.renderTable();
        this.renderPagination();
      }
    });

    // Page size change handler
    $("#page-size").val(this.pageSize);
    $("#page-size").on("change", (e) => {
      this.pageSize = parseInt(e.target.value, 10);
      $("#page-number").val(1);
      this.currentPage = 1;
      this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 0;
      this.renderTable();
      this.renderPagination();
    });

    // Page number change handler
    $("#page-number").on("input", (e) => {
      const pageNumber = parseInt(e.target.value);
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.currentPage = pageNumber;
        this.renderTable();
        this.renderPagination();
      } else {
        e.target.value = this.currentPage;
      }
    });
  }

  updateDataViewWithSort() {
    if (!this.dataView || !this.sortDefault.column) return;
    const currentData = this.dataView.getItems();
    currentData.sort((a, b) => {
      // First, check if one is selected and the other is not
      const aSelected = this.selectedRowIds.includes(a.id);
      const bSelected = this.selectedRowIds.includes(b.id);

      if (aSelected && !bSelected) return -1; // a (selected) comes before b (not selected)
      if (!aSelected && bSelected) return 1;  // b (selected) comes before a (not selected)

      // If both have the same selection status, apply original sorting
      return compareForSort(a[this.sortDefault.column], b[this.sortDefault.column], this.sortDefault.direction);
    });
    this.dataView.setItems(currentData);
    if (this.grid) {
      this.grid.invalidateAllRows();
      this.grid.render();
    }
  }

  renderPaginationContent() {
    const paginationContainer = document.getElementById(this.paginationTarget);
    if (!paginationContainer) {
      console.log(`Pagination target ${this.paginationTarget} not found.`);
      return;
    }

    paginationContainer.innerHTML = `
      <div class="flex justify-between items-center gap-2 w-full">
        <div class="flex items-center gap-1">
          <button id="prev-page" type="button" ${
            this.currentPage < 2 ? "disabled" : ""
          } href="#" class="flex items-center justify-center px-3 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
            <svg class="w-3.5 h-3.5 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5H1m0 0 4 4M1 5l4-4"/>
            </svg>
          </button>
          <input type="text" id="page-number" class="w-12 h-10 px-2 text-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" value="${
            this.currentPage
          }" />
          <button id="next-page" type="button" ${
            this.currentPage >= this.totalPages ? "disabled" : ""
          } href="#" class="flex items-center justify-center px-3 h-8 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white">
            <svg class="w-3.5 h-3.5 rtl:rotate-180" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 10">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 5h12m0 0L9 1m4 4L9 9"/>
            </svg>
          </button>
          <span class="text-gray-700">Total Pages: ${this.totalPages}</span>
        </div>
        <select id="page-size" class="w-15 p-2.5 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
    `;

    document.getElementById("page-size").value = this.pageSize;
  }

  onSelectedRowsChanged(callback) {
    this.onSelectedRowsChangedCallback = callback;
  }

  onSort(callback) {
    this.onSortCallback = callback;
  }

  onGridClick(callback) {
    this.grid.onClick.subscribe((e, args) => {
      const cell = this.grid.getCellFromEvent(e);
      if (cell) {
        const row = this.dataView.getItem(cell.row);
        callback(row, cell);
      }
    });
  }

  onSearch(searchTerm) {
    this.searchTerm = searchTerm;

    // Update pagination counts if pagination is enabled
    if (this.pageSize) {
      // Include injected selected items in the count
      const currentDataIds = new Set(this.data.map((item) => item.id));
      const missingSelectedItems = this.selectedRowData.filter((item) => {
        const key = selectionStorageRowKey(item);
        return key != null && !currentDataIds.has(key);
      });
      // Transform missing items to match column field format (underscores → dashes)
      const transformedMissingItems = missingSelectedItems.length > 0 
        ? this.jsonToTableData(missingSelectedItems, false) 
        : [];
      const allData = [...transformedMissingItems, ...this.data];

      // Calculate total count for filtered data
      let filteredCount = 0;

      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        filteredCount = allData.filter((item) => {
          for (const key in item) {
            if (this.selectedColumns.includes(key) && item.hasOwnProperty(key) && item[key] != null) {
              var value = item[key].toString().toLowerCase();
              if (value.indexOf(searchTermLower) !== -1) {
                return true;
              }
            }
          }
          return false;
        }).length;
      } else {
        filteredCount = allData.length;
      }

      this.totalCount = filteredCount;
      this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 0;
      this.currentPage = 1; // Reset to first page when searching
    }

    this.renderTable();
    if (this.pageSize) {
      this.renderPagination();
    }
  }

  clearSelection() {
    if (this.grid) {
      this.grid.setSelectedRows([]);
    }
    this.selectedRowData = [];
    this.selectedRowIds = [];
    localStorage.setItem(this.selectionStorageKey, JSON.stringify([]));
    localStorage.setItem(this.selectionIdsStorageKey, JSON.stringify([]));
    this.renderTable();
    if (this.pageSize) {
      this.renderPagination();
    }
  }
}

export default SlickGridTable;
